import {
	type LigneMouvement,
	type Mouvement,
	type ParametreGeneralEntite,
	Sens,
} from '@mairistem/comptabilite';
import { BaseProfilEcriture, type Gabarit } from '@mairistem/finances-utils';
import type { ErrorField } from '@mairistem/liste';
import { isEmpty } from 'lodash';
import { ASAP } from '../../../../constants/mouvements/asapOptions';
import {
	MODE_REGLEMENT,
	type ModeReglementCode,
	type ModeReglementType,
} from '../../../../constants/mouvements/modeReglements';
import type PiecePanel from '../../../../interfaces/pieces/mouvements/piecePanel';
import Imputation from '../Imputation';

class BaseProfilEcritureMouvement extends BaseProfilEcriture {
	code = '';

	entite: ParametreGeneralEntite;

	imputation = new Imputation();

	isLiaisonVisible = true;

	defaultModeReglement: ModeReglementCode | null = null;

	modeDeReglement: string[] = [];

	naturePiece: string = '';

	panels: PiecePanel[] = [];

	typeBordereau = '';

	typePiece = '';

	codesProduitLocal: string[] = [];

	/**
	 * Fonction permettant de mettre à jour les informations du mouvement dès
	 * le changement d'un profil
	 * @returns Retourne les attributs par défauts
	 */
	onProfilSelect(_data: Partial<Mouvement>) {
		return {
			...(!isEmpty(this.naturePiece) && { naturePiece: this.naturePiece }),
			...(!isEmpty(this.typePiece) && { typePiece: this.typePiece }),
		};
	}

	/**
	 * Permet de récupérer le mode de réglement par rapport au sens
	 * @returns liste de mode de réglement
	 */
	getModeReglement(sens: string) {
		let optionsModeReglement = null;
		if (sens === Sens.DEPENSE) {
			optionsModeReglement = MODE_REGLEMENT.DEPENSE_LIST;
		} else {
			optionsModeReglement = MODE_REGLEMENT.RECETTE_LIST;
		}

		if (!isEmpty(this.modeDeReglement)) {
			optionsModeReglement = optionsModeReglement.filter(
				(mode: ModeReglementType) => this.modeDeReglement.includes(mode.key),
			);
		}

		return optionsModeReglement;
	}

	getLigneDefaultAttributes(): object {
		return {
			...(!isEmpty(this.defaultModeReglement) && {
				modeReglement: this.defaultModeReglement,
			}),
		};
	}

	/**
	 * Contrôles front du mouvement liés au profil d'écriture :
	 * - les blocs (panels) déclarés obligatoires doivent être renseignés ;
	 * - les contrôles propres à chaque ligne (cf. checkLigne).
	 * @param mouvement Le mouvement en cours de saisie
	 * @returns La liste des erreurs à corriger avant de valider la fiche
	 */
	check(mouvement: Mouvement): ErrorField[] {
		const errors: ErrorField[] = [];

		errors.push(...this.checkPanels(mouvement));

		mouvement?.lignes?.forEach((ligne: LigneMouvement) => {
			errors.push(...this.checkLigne(ligne));
		});

		return errors;
	}

	/**
	 * Contrôle bloquant des blocs (panels) du profil d'écriture.
	 *
	 * Chaque bloc porte sa propre validation via `check` : obligation de saisie
	 * d'un bloc obligatoire, et règles de complétude/cohérence propres au bloc
	 * (cf. DelaiPaiementPanel). Le profil se contente d'agréger ces contrôles : la
	 * règle se déduit de la déclaration des blocs, aucun code spécifique par
	 * profil n'est nécessaire.
	 * @param mouvement Le mouvement en cours de saisie
	 * @returns Les erreurs remontées par les blocs du profil
	 */
	checkPanels(mouvement: Mouvement): ErrorField[] {
		const errors: ErrorField[] = [];

		this.panels.forEach((panel) => {
			errors.push(...panel.check(mouvement));
		});

		return errors;
	}

	checkLigne(_ligne: LigneMouvement): ErrorField[] {
		return [];
	}

	// #region panelDefaultAttributes
	getAsapEditionDefaultAttributes(
		isTiersMulti: boolean,
		piece: Mouvement,
		isChangedTiers?: boolean,
	) {
		let value = ASAP.A_EDITER;

		if (isTiersMulti) {
			value = ASAP.NON_DEMATERIALISEE;
		} else if (isEmpty(piece?.tiers?.categoriePes)) {
			value = ASAP.COMPTABLE;
		} else if (piece?.tiers?.categoriePes?.startsWith('2')) {
			value = ASAP.CHORUS_PRO;
		}

		return {
			asapEdition: value,
			...((isChangedTiers || value !== ASAP.CHORUS_PRO) && {
				asapNumeroEngagement: null,
			}),
			...(isEmpty(piece?.asapCodeService) && {
				asapCodeService: value !== ASAP.CHORUS_PRO ? null : 'FACTURE_PUBLIQUE',
			}),
		};
	}

	getDetailDeFacturationDefaultAttributes(piece: Mouvement) {
		return {
			lfacLibelle: piece?.objetPiece?.substring(0, 199),
			lfacDate: piece?.dateDepartDelaiGlobalPaiement,
		};
	}

	getPeriodeCouverteDefaultAttributes(annee: number) {
		return {
			dateDebutCharge: `${annee}-01-01`,
			dateFinCharge: `${annee}-12-31`,
		};
	}

	// #endregion
	getNaturesGabarit(_gabaritsPj: Gabarit[]) {
		return [];
	}
}

export default BaseProfilEcritureMouvement;
